import './workQueue/workQueue.css';

import {
  ExclusionTypeOption,
  ListOption,
  NeverBookableDepartment,
  StatusOption,
  StatusStage,
  WorkQueueLists,
  publishWorkQueueLists,
  useWorkQueueLists
} from './workQueue/workQueueLists';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import axios from 'axios';

interface AuditEntry {
  changedAt: string;
  actorName: string;
  actorEmail: string;
  changes: string[];
}

const STAGE_LABELS: Record<StatusStage, string> = {
  open: 'Open',
  'in-progress': 'In progress',
  closed: 'Closed'
};

const move = <T,>(items: T[], index: number, offset: number): T[] => {
  const target = index + offset;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

interface OptionTableProps<T extends ListOption> {
  title: string;
  singular: string;
  description: ReactNode;
  items: T[];
  onChange: (items: T[]) => void;
  /** Values that existed when the page loaded; only those are locked against removal. */
  saved: string[];
  extraHeader?: ReactNode;
  extraCells?: (item: T, update: (patch: Partial<T>) => void) => ReactNode;
  locked?: (item: T) => boolean;
  note?: (item: T) => string | null;
  addFields?: ReactNode;
  onAdd: (name: string) => string | null;
}

/** One editable vocabulary: relabel, reorder, retire, add. */
function OptionTable<T extends ListOption>({
  title, singular, description, items, onChange, saved, extraHeader, extraCells, locked, note, addFields, onAdd
}: OptionTableProps<T>) {
  const [name, setName] = useState('');
  const [addError, setAddError] = useState('');
  const update = (index: number) => (patch: Partial<T>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <div className="wql-block">
      <h3 className="wql-title">{title}</h3>
      <p className="wql-description">{description}</p>
      <table className="wql-table">
        <thead>
          <tr>
            <th aria-label="Order" />
            <th>Label</th>
            {extraHeader}
            <th>Active</th>
            <th aria-label="Notes" />
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const isLocked = locked?.(item) ?? false;
            const isNew = !saved.includes(item.value);
            return (
              <tr key={item.value} className={item.retired ? 'wql-retired' : ''}>
                <td className="wql-order">
                  <button type="button" aria-label="Move up" disabled={index === 0} onClick={() => onChange(move(items, index, -1))}>▲</button>
                  <button type="button" aria-label="Move down" disabled={index === items.length - 1} onClick={() => onChange(move(items, index, 1))}>▼</button>
                </td>
                <td>
                  <input
                    className="settings-input"
                    value={item.label}
                    maxLength={60}
                    onChange={(event) => update(index)({ label: event.target.value } as Partial<T>)}
                    aria-label={`Label for ${item.value}`}
                  />
                  {item.label !== item.value && <div className="wql-value">stored as &ldquo;{item.value}&rdquo;</div>}
                </td>
                {extraCells?.(item, update(index))}
                <td>
                  {isNew ? (
                    <button type="button" className="link-button" onClick={() => onChange(items.filter((_, i) => i !== index))}>
                      Remove
                    </button>
                  ) : (
                    <input
                      type="checkbox"
                      checked={!item.retired}
                      disabled={isLocked}
                      title={isLocked ? 'Core value - the app depends on it.' : 'Untick to retire: hidden from pickers, kept on history.'}
                      onChange={(event) => update(index)({ retired: !event.target.checked } as Partial<T>)}
                      aria-label={`${item.label} active`}
                    />
                  )}
                </td>
                <td className="wql-note">{note?.(item)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="wql-add">
        <input
          className="settings-input"
          value={name}
          maxLength={60}
          placeholder={`New ${singular}`}
          onChange={(event) => { setName(event.target.value); setAddError(''); }}
          aria-label={`New ${singular}`}
        />
        {addFields}
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={() => {
            const error = onAdd(name.trim());
            if (error) setAddError(error);
            else setName('');
          }}
        >
          Add
        </button>
        {addError && <span className="wql-error">{addError}</span>}
      </div>
    </div>
  );
}

/**
 * Settings section for the work-queue vocabularies. Values are written onto
 * notes, so existing ones can be relabelled, reordered or retired but not
 * removed; a value added in this session can be removed until it is saved.
 */
export default function WorkQueueListsSettings() {
  const lists = useWorkQueueLists();
  const [draft, setDraft] = useState<WorkQueueLists>(lists);
  const [edited, setEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [showAllAudit, setShowAllAudit] = useState(false);
  const [newStage, setNewStage] = useState<StatusStage>('in-progress');
  const [newRequiresUntil, setNewRequiresUntil] = useState(false);

  // Follows the server copy until someone starts editing.
  useEffect(() => {
    if (!edited) setDraft(lists);
  }, [lists, edited]);

  const loadAudit = useCallback(async () => {
    try {
      const response = await axios.get<AuditEntry[]>('/api/settings/work-queue-lists/audit', { withCredentials: true });
      setAudit(response.data);
    } catch {
      setAudit([]);
    }
  }, []);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const change = (patch: Partial<WorkQueueLists>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setEdited(true);
    setNotice('');
  };

  const duplicate = (items: ListOption[], name: string): string | null => {
    if (!name) return 'Enter a name.';
    if (items.some((item) => item.value.toLowerCase() === name.toLowerCase() || item.label.toLowerCase() === name.toLowerCase())) {
      return `"${name}" already exists.`;
    }
    return null;
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await axios.put<WorkQueueLists>('/api/settings/work-queue-lists', draft, { withCredentials: true });
      publishWorkQueueLists(response.data);
      setDraft(response.data);
      setEdited(false);
      setNotice('Saved. Both work queues use the new lists now.');
      loadAudit();
    } catch (saveError: any) {
      setError(saveError?.response?.data?.error || 'Could not save the lists.');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setDraft(lists);
    setEdited(false);
    setError('');
    setNotice('');
  };

  const savedValues = (items: ListOption[]) => items.map((item) => item.value);

  const updateDepartment = (index: number, patch: Partial<NeverBookableDepartment>) =>
    change({
      neverBookableDepartments: draft.neverBookableDepartments.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
    });
  const savedDepartmentIds = lists.neverBookableDepartments.map((entry) => entry.departmentId);

  return (
    <div className="settings-section">
      <h2 className="settings-heading">Work queue lists</h2>
      <p className="wql-description">
        The values both work queues offer. Changes apply as soon as they are saved - no deployment.
        Values already used on notes can be relabelled, reordered or retired (hidden from pickers but
        kept on history); they cannot be deleted.
      </p>

      <OptionTable<StatusOption>
        title="Statuses"
        singular="status"
        description={
          <>
            The stage decides how a status counts: <strong>Open</strong> and <strong>In progress</strong> are
            open work, <strong>Closed</strong> moves the item to History. New, Resolved and Auto-Resolved are
            core statuses the rescan relies on.
          </>
        }
        items={draft.statuses}
        saved={savedValues(lists.statuses)}
        onChange={(statuses) => change({ statuses })}
        locked={(item) => item.locked}
        note={(item) => (item.systemOnly ? 'Set by the rescan only' : item.locked ? 'Core status' : null)}
        extraHeader={<th>Stage</th>}
        extraCells={(item, update) => (
          <td>
            <select
              className="settings-input"
              value={item.stage}
              disabled={item.locked}
              onChange={(event) => update({ stage: event.target.value as StatusStage })}
              aria-label={`Stage for ${item.label}`}
            >
              {(Object.keys(STAGE_LABELS) as StatusStage[]).map((stage) => (
                <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>
              ))}
            </select>
          </td>
        )}
        addFields={
          <select className="settings-input" value={newStage} onChange={(event) => setNewStage(event.target.value as StatusStage)} aria-label="Stage for the new status">
            {(Object.keys(STAGE_LABELS) as StatusStage[]).map((stage) => (
              <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>
            ))}
          </select>
        }
        onAdd={(name) => {
          const problem = duplicate(draft.statuses, name);
          if (problem) return problem;
          // Keeps closed statuses at the end, where the workflow finishes.
          const closedAt = draft.statuses.findIndex((status) => status.stage === 'closed');
          const entry: StatusOption = { value: name, label: name, retired: false, stage: newStage, locked: false, systemOnly: false };
          const statuses = [...draft.statuses];
          statuses.splice(newStage === 'closed' || closedAt < 0 ? statuses.length : closedAt, 0, entry);
          change({ statuses });
          return null;
        }}
      />

      <OptionTable<ListOption>
        title="Assigned teams"
        singular="team"
        description="Teams an item can be routed to. Assigned to (a person) is picked from the directory and needs no list."
        items={draft.teams}
        saved={savedValues(lists.teams)}
        onChange={(teams) => change({ teams })}
        onAdd={(name) => {
          const problem = duplicate(draft.teams, name);
          if (problem) return problem;
          change({ teams: [...draft.teams, { value: name, label: name, retired: false }] });
          return null;
        }}
      />

      <OptionTable<ExclusionTypeOption>
        title="Exclusion types"
        singular="exclusion type"
        description="Why an item is set aside from open work. Every exclusion can carry an end date; types that require one cannot be saved without it. On the end date the item returns to open work."
        items={draft.exclusionTypes}
        saved={savedValues(lists.exclusionTypes)}
        onChange={(exclusionTypes) => change({ exclusionTypes })}
        extraHeader={<th>End date</th>}
        extraCells={(item, update) => (
          <td>
            <label className="wql-inline">
              <input
                type="checkbox"
                checked={item.requiresUntil}
                onChange={(event) => update({ requiresUntil: event.target.checked })}
              />
              required
            </label>
          </td>
        )}
        addFields={
          <label className="wql-inline">
            <input type="checkbox" checked={newRequiresUntil} onChange={(event) => setNewRequiresUntil(event.target.checked)} />
            end date required
          </label>
        }
        onAdd={(name) => {
          const problem = duplicate(draft.exclusionTypes, name);
          if (problem) return problem;
          change({
            exclusionTypes: [...draft.exclusionTypes, { value: name, label: name, retired: false, requiresUntil: newRequiresUntil }]
          });
          return null;
        }}
      />

      <div className="wql-block">
        <h3 className="wql-title">Never-bookable departments</h3>
        <p className="wql-description">
          Blocks on these departments are not counted as findings, and the departments are never ranked as
          opportunities. Only add a department the data shows can never be bookable by design - being blocked is
          not enough. Leave the end date empty to exclude indefinitely.
        </p>
        <table className="wql-table">
          <thead>
            <tr>
              <th>Epic dept ID</th>
              <th>Name</th>
              <th>Why it is never bookable</th>
              <th>Evidence (optional)</th>
              <th>Excluded until</th>
              <th aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {draft.neverBookableDepartments.map((entry, index) => {
              const isNew = !savedDepartmentIds.includes(entry.departmentId) || entry.departmentId === '';
              return (
                <tr key={`${index}-${savedDepartmentIds.includes(entry.departmentId) ? entry.departmentId : 'new'}`}>
                  <td>
                    <input
                      className="settings-input wql-narrow"
                      value={entry.departmentId}
                      readOnly={!isNew}
                      inputMode="numeric"
                      onChange={(event) => updateDepartment(index, { departmentId: event.target.value.replace(/\D/g, '') })}
                      aria-label="Epic department ID"
                    />
                  </td>
                  <td>
                    <input
                      className="settings-input"
                      value={entry.name}
                      maxLength={120}
                      onChange={(event) => updateDepartment(index, { name: event.target.value })}
                      aria-label="Department name"
                    />
                  </td>
                  <td>
                    <textarea
                      className="settings-input wql-text"
                      value={entry.rationale}
                      maxLength={1000}
                      rows={2}
                      onChange={(event) => updateDepartment(index, { rationale: event.target.value })}
                      aria-label="Why it is never bookable"
                    />
                  </td>
                  <td>
                    <textarea
                      className="settings-input wql-text"
                      value={entry.evidence}
                      maxLength={2000}
                      rows={2}
                      onChange={(event) => updateDepartment(index, { evidence: event.target.value })}
                      aria-label="Evidence"
                    />
                  </td>
                  <td>
                    <input
                      className="settings-input"
                      type="date"
                      value={entry.excludedUntil ?? ''}
                      onChange={(event) => updateDepartment(index, { excludedUntil: event.target.value || null })}
                      aria-label="Excluded until"
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        change({ neverBookableDepartments: draft.neverBookableDepartments.filter((_, i) => i !== index) })
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="wql-add">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() =>
              change({
                neverBookableDepartments: [
                  ...draft.neverBookableDepartments,
                  { departmentId: '', name: '', rationale: '', evidence: '', excludedUntil: null }
                ]
              })
            }
          >
            Add department
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      <div className="settings-actions">
        <button className="btn btn-primary" type="button" onClick={save} disabled={saving || !edited}>
          {saving ? 'Saving…' : 'Save lists'}
        </button>
        {edited && (
          <button className="btn btn-secondary" type="button" onClick={discard} disabled={saving}>
            Discard changes
          </button>
        )}
        {notice && <span className="settings-notice">{notice}</span>}
        {lists.updatedAt && (
          <span className="settings-updated">
            Last changed {new Date(lists.updatedAt).toLocaleString()}
            {lists.updatedBy ? ` by ${lists.updatedBy}` : ''}
          </span>
        )}
      </div>

      <h3 className="wql-title" style={{ marginTop: '1.5rem' }}>Change history</h3>
      {audit.length === 0 ? (
        <p className="settings-empty">No changes recorded yet.</p>
      ) : (
        <ul className="settings-history">
          {(showAllAudit ? audit : audit.slice(0, 5)).map((entry) => (
            <li key={`${entry.changedAt}-${entry.actorEmail}`}>
              <div className="settings-digest-meta">
                <strong>{entry.actorName}</strong> · {new Date(entry.changedAt).toLocaleString()}
              </div>
              <ul className="wql-changes">
                {entry.changes.map((line, index) => <li key={index}>{line}</li>)}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {audit.length > 5 && (
        <button type="button" className="link-button" onClick={() => setShowAllAudit(!showAllAudit)}>
          {showAllAudit ? 'Show fewer' : `Show all ${audit.length}`}
        </button>
      )}
    </div>
  );
}
